import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import * as Scenes from "oc-threejs/Scenes";
// import {InstancedUniformsMesh} from "three-instanced-uniforms-mesh";
import { InstancedUniformsMesh } from "oc-jaco3d/three-instanced-uniforms-mesh/InstancedUniformsMesh";
import { getUvTransform, IGetUvTransformOptions } from "./CalculateMaterialUvTransformShuffles";
import { GetMaterialUvTransform } from "oc-threejs/materials/GetMaterialUvTransform";

import _ from "lodash";
import {
  BufferGeometry,
  Camera,
  Group,
  Material,
  Matrix3,
  Matrix4,
  Mesh,
  Object3D,
  Scene,
  WebGLRenderer,
} from "three";

export interface MModelMaterialRelation {
  /** Die konkrete Objekt oder Pfosten */
  id: number;
  Objekt: { id: number; key: string };
  Model: { id: number; key: string; path: string };
  Mirrored?: MModelMaterialRelation;
  MaterialMap?: Map<string, string>;
  Json?: any;
  adjacency?: Record<string, Set<any>>;
}

// Interfaces for Global objects
interface IApp {
  checkMMRNotUndefined: (mmr: MModelMaterialRelation) => MModelMaterialRelation;
}
declare const APP: IApp;

// Interfaces for data structures
interface IInstance {
  matrixWorld: Matrix4;
  opacity: number;
  uvTransform: Matrix3;
}

interface IInstancedCollection {
  key: number;
  name: string;
  geometry: BufferGeometry;
  material: Material & { transparent: boolean; needsUpdate: boolean };
  materialUvTransform: Matrix3;
  object: Mesh;
  instances: IInstance[];
  instancedMesh: InstancedUniformsMesh | null;
  instancedMeshMaximumCount: number;
}

type InstancedCollectionMap = Map<number, IInstancedCollection>;

// Extend THREE.Scene to include our custom map
interface IExtendedScene extends Scene {
  instancedCollectionMap?: InstancedCollectionMap;
}

// Interface for Ghost Object (ModelGhost3d)
interface IGhostObject extends Object3D {
  isModelGhost3d?: boolean;
  model3d?: {
    group: Group;
  };
  jaco?: {
    shuffleMaterialIndex: number;
  };
  modelMaterialRelation?: any;
}

export const monkeyPatchScene = () => {
  const oldOnBeforeRender = Scene.prototype.onBeforeRender;

  Scene.prototype.onBeforeRender = function (
    this: IExtendedScene,
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    geometry: BufferGeometry,
    material: Material,
    group: Group,
  ) {
    if (this.instancedCollectionMap === undefined) {
      this.instancedCollectionMap = new Map();
    }
    oldOnBeforeRender.call(this, renderer, scene, camera, geometry, material, group);
    this.instancedCollectionMap.forEach((c) => (c.instances.length = 0));
    traverseScene(scene, 1, this.instancedCollectionMap);
    renderInstancedMeshes(scene, this.instancedCollectionMap);
    removeUnusedInstancedMeshes(this.instancedCollectionMap);
  };
};

export function traverseSceneOnceForGhostInstances(scene: Scene) {
  const instancedCollectionMap: InstancedCollectionMap = new Map();
  traverseScene(scene, 1, instancedCollectionMap);
  renderInstancedMeshes(scene, instancedCollectionMap);
  removeUnusedInstancedMeshes(instancedCollectionMap);
}

const traverseScene = (
  object: Object3D,
  opacity: number,
  instancedCollectionMap: InstancedCollectionMap,
) => {
  if (object.visible) {
    // Cast to access properties like opacity if they are not on Object3D directly (Object3D has no opacity, usually Material or extended object)
    // Actually Object3D doesn't have opacity property, only Material does.
    // Logic seems to assume object has opacity (perhaps extended or mixed in).
    // Using (object as any) for properties not on Object3D
    opacity *= (object as any).opacity ?? 1;

    const ghost = object as IGhostObject;
    if (ghost.isModelGhost3d) {
      if (ghost.model3d?.group) {
        // also, das hier ist bad. object.getMeta() liefert ein Model, und das hat mehr als ein mmr
        const mmr = APP.checkMMRNotUndefined(ghost.modelMaterialRelation);
        const mmrId = mmr.id;
        const shuffleMap = mmr.Json?.ShuffleMap;
        const shuffleMaterialIndex = ghost.jaco?.shuffleMaterialIndex ?? 0;
        const shuffleInfo: IGetUvTransformOptions = { mmrId, shuffleMap, shuffleMaterialIndex };
        traverseGhost(
          ghost,
          ghost.model3d.group,
          ghost.matrixWorld,
          opacity,
          shuffleInfo,
          instancedCollectionMap,
        );
      }
    } else {
      object.children.forEach((child) => traverseScene(child, opacity, instancedCollectionMap));
    }
  }
};

const traverseGhost = (
  ghost3d: IGhostObject,
  object: Object3D,
  matrixWorld: Matrix4,
  opacity: number,
  shuffleInfo: IGetUvTransformOptions,
  instancedCollectionMap: InstancedCollectionMap,
) => {
  if (object.visible) {
    opacity *= (object as any).opacity ?? 1;
    matrixWorld = matrixWorld.clone().multiply(object.matrix);

    // Check if it's a mesh with geometry and material
    const mesh = object as Mesh;
    if (mesh.geometry && mesh.material) {
      const geometry = mesh.geometry;
      const material = (
        Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
      ) as Material & {
        transparent: boolean;
        needsUpdate: boolean;
      };

      const key = Maths.pairing(geometry.id, material.id);
      let c = instancedCollectionMap.get(key);
      if (c == null) {
        // if (ghost3d.key == 347) {
        //   console.log("traverseGhost c==null for ", key, "pair <=", ghost3d.key);
        // }
        const materialUvTransform = GetMaterialUvTransform(material);
        c = {
          key,
          name: object.name + ":" + material.name,
          geometry,
          material: material as Material & { transparent: boolean; needsUpdate: boolean }, // Cast for mutable properties usage later
          materialUvTransform,
          object: mesh,
          instances: [],
          instancedMesh: null,
          instancedMeshMaximumCount: 0,
        };
        instancedCollectionMap.set(key, c);
      }
      const uvTransform = getUvTransform(material, shuffleInfo);
      const instance: IInstance = {
        matrixWorld,
        opacity,
        uvTransform,
      };
      c.instances.push(instance);
    }
    object.children.forEach((child) =>
      traverseGhost(ghost3d, child, matrixWorld, opacity, shuffleInfo, instancedCollectionMap),
    );
  }
};

const opacityTreshold = 0.9999;

const renderInstancedMeshes = (
  scene: Scene | Object3D,
  instancedCollectionMap: InstancedCollectionMap,
) => {
  const matrix3_uv_transform = new Matrix3();
  instancedCollectionMap.forEach((c) => {
    if (c.instances.length > c.instancedMeshMaximumCount) {
      removeInstancedMesh(c);
      c.instancedMeshMaximumCount = c.instances.length + 50;
      c.instancedMesh = new InstancedUniformsMesh(
        c.geometry,
        c.material,
        c.instancedMeshMaximumCount,
      );
      c.instancedMesh!.frustumCulled = false;
      c.instancedMesh!.castShadow = c.object.castShadow;
      c.instancedMesh!.receiveShadow = c.object.receiveShadow;
      c.instancedMesh!.renderOrder = c.object.renderOrder;
      c.instancedMesh!.name = "GhostInstances(" + c.instancedMeshMaximumCount + "):" + c.name;
      // c.instancedMesh!.instancedCollection = c; // Assuming we can attach properties to mesh or remove if unncessary.
      // TS: Property 'instancedCollection' does not exist on type 'InstancedUniformsMesh'.
      // If it's used for debugging, we can cast.
      (c.instancedMesh as any).instancedCollection = c;

      scene.add(c.instancedMesh!);
    }

    if (c.instancedMesh) {
      if (
        !c.material.transparent &&
        c.instances.some((instance) => instance.opacity < opacityTreshold)
      ) {
        c.material.transparent = true;
        c.material.needsUpdate = true;
      }
      c.instancedMesh.count = c.instances.length;
      let index = 0;
      c.instances.forEach((instance) => {
        c.instancedMesh!.setMatrixAt(index, instance.matrixWorld);
        if (c.material.transparent) {
          c.instancedMesh!.setUniformAt("opacity", index, instance.opacity);
        }
        if (c.materialUvTransform && instance.uvTransform) {
          matrix3_uv_transform.multiplyMatrices(c.materialUvTransform, instance.uvTransform);
          c.instancedMesh!.setUniformAt("mapTransform", index, matrix3_uv_transform);
        }
        index++;
      });
      c.instancedMesh.instanceMatrix.needsUpdate = true;
    }
  });
};

const removeUnusedInstancedMeshes = (instancedCollectionMap: InstancedCollectionMap) => {
  Array.from(instancedCollectionMap.values())
    .filter((c) => c.instances.length == 0)
    .forEach((c) => {
      instancedCollectionMap.delete(c.key);
      removeInstancedMesh(c);
    });
};

const removeInstancedMesh = (c: IInstancedCollection) => {
  if (c.instancedMesh) {
    Scenes.remove(c.instancedMesh);
    c.instancedMesh.dispose();
    c.instancedMesh = null;
  }
};
